import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import Dropzone from 'react-dropzone';
import classnames from 'classnames';

import { Loading } from 'components/Loading';
import { Modal, Button, Spacer } from '@ontruck/orc';

import * as http from 'utils/http';
import { createJourneyLog } from 'redux/modules/log/effects';
import { readFile } from 'utils/file';

import config from 'config';

import ImageItem from './image-item';
import PDFItem from './pdf-item';

import _ from 'lodash';
import { logChangesGenerator } from 'redux/modules/log/log-changes';

import style from './style.module.scss';

class CargoManifestForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      uploading: false,
      error: false,
      serverError: false,
      baseOrdinal: 0,
      imageFiles: [],
      pendingImageFiles: [],
      pdfFiles: [],
    };
    this.onDrop = this.onDrop.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.getImages = this.getImages.bind(this);
    this.onDeletePODPdf = this.onDeletePODPdf.bind(this);
    this.getFilesTypeAccepted = this.getFilesTypeAccepted.bind(this);
  }

  getImages() {
    const { journey, cargoManifest } = this.props;

    if (!journey || !cargoManifest) {
      return;
    }

    this.setState({ loading: true }, () => {
      http.get(cargoManifest.images_url).then(({ results: imageFiles }) => {
        this.setState({
          loading: false,
          baseOrdinal:
            1 + (imageFiles || []).map((f) => f.ordinal).reduce((a, b) => Math.max(a, b), 0),
          imageFiles,
        });
      });
    });
  }

  componentDidMount() {
    const { journey, open } = this.props;

    if (!journey || !open) {
      return;
    }

    this.getImages();
  }

  componentWillReceiveProps(newProps) {
    if (newProps.open !== this.props.open) {
      this.getImages();
    }
  }

  onSubmit() {
    const { createJourneyLog, journey, cargoManifest, stopId } = this.props;

    const { pdfFiles } = this.state;

    if (!journey) {
      return;
    }

    const getPdfFilesData = () => {
      return new Promise((resolve, reject) => {
        if (pdfFiles.length > 0) {
          readFile(pdfFiles[0]).then((fileText) => {
            resolve({ files: [fileText], is_pdf: true });
          });
        } else {
          resolve({});
        }
      });
    };

    this.setState({ loading: true, serverError: false }, () => {
      getPdfFilesData().then((getPdfFilesData) => {
        http
          .post(cargoManifest.generate_cargo_manifest_url, getPdfFilesData)
          .then(() => http.get(journey.url))
          .then((res) => {
            const stop_company_name = _.get(journey, `stops.${stopId}.address.company_name`);
            const action = 'UPLOAD_CARGO_MANIFEST';
            const changes = logChangesGenerator.get(action, {
              stop_company_name: stop_company_name,
              stop: stopId,
            });
            createJourneyLog({
              action,
              journeyId: journey.id,
              changes,
            });
            return res;
          })
          .then((payload) => this.setState({ loading: false }, () => this.props.onCreate(payload)))
          .catch((error) => this.setState({ serverError: true, loading: false }));
      });
    });
  }

  onDrop(acceptedFiles, rejectedFiles) {
    const { journey, cargoManifest } = this.props;
    const loading = this.state.loading;
    const initialPending = this.state.pendingImageFiles || [];
    const baseOrdinal = this.state.baseOrdinal + initialPending.length;
    const baseId = Date.now();
    const error = rejectedFiles && rejectedFiles.length > 0;

    const allAcceptedFilesAreImages =
      acceptedFiles.filter((file) => /image\/*/.test(file.type)).length === acceptedFiles.length &&
      acceptedFiles.length > 0;
    const onlyOnePDFIsUploaded =
      acceptedFiles.filter((file) => /application\/pdf/.test(file.type)).length === 1 &&
      acceptedFiles.length === 1;

    if (!journey || loading) {
      return;
    }

    // A POD PDF file is uploaded => upload it directly when click on save button
    if (onlyOnePDFIsUploaded && !error) {
      this.setState({
        error,
        pdfFiles: acceptedFiles,
      });
    }

    // All uploaded files are images => upload them to generate a pdf later
    else if (allAcceptedFilesAreImages) {
      this.setState(
        {
          error,
          uploading: true,
          pendingImageFiles: [
            ...initialPending,
            ...acceptedFiles.map((file, index) => ({ file, id: `${baseId}/${index}` })),
          ],
        },
        () => {
          return Promise.all(
            acceptedFiles.map((file, index) => {
              return readFile(file).then((fileText) => {
                return http
                  .post(cargoManifest.images_url, {
                    ordinal: baseOrdinal + index,
                    image: fileText,
                  })
                  .then((backendFile) => {
                    this.setState({
                      baseOrdinal: this.state.baseOrdinal + 1,
                      imageFiles: this.state.imageFiles.concat(backendFile),
                      pendingImageFiles: this.state.pendingImageFiles.filter(
                        ({ id }) => String(id) !== `${baseId}/${index}`
                      ),
                    });
                  });
              });
            })
          ).then(() =>
            this.setState({
              error: false,
              uploading: false,
            })
          );
        }
      );
    }

    // Some error ocurred
    else {
      this.setState({
        error: true,
        uploading: false,
      });
    }
  }

  onDelete(id) {
    const { journey, cargoManifest } = this.props;

    const { imageFiles, loading } = this.state;

    if (!journey || loading || typeof id != 'number') {
      return;
    }

    return http._delete(`${cargoManifest.images_url}${id}/`).then(() =>
      this.setState({
        imageFiles: imageFiles.filter(({ ordinal }) => parseInt(ordinal, 10) !== parseInt(id, 10)),
      })
    );
  }

  onDeletePODPdf() {
    this.setState({ pdfFiles: [], serverError: false });
  }

  getFilesTypeAccepted() {
    if (this.state.pdfFiles.length > 0) {
      return '';
    }

    if (this.state.imageFiles.length > 0 || this.state.uploading) {
      return 'image/jpg, image/jpeg';
    }

    return 'image/jpg, image/jpeg, application/pdf';
  }

  render() {
    const { open, onClose, intl } = this.props;

    const {
      loading,
      uploading,
      error,
      serverError = false,
      imageFiles = [],
      pendingImageFiles = [],
      pdfFiles = [],
    } = this.state;

    const t = intl.messages.components.CargoManifestForm;

    const localClassName = {
      dropzone: classnames(style.dropzone, {
        [style.dropzoneError]: error,
      }),
    };

    return (
      <div>
        <Modal show={open} onClose={onClose} size="L" headerComponent={t.upload}>
          {serverError ? (
            <span className={style.dropzoneErrorTitle}>{t.error.corrupt_file}</span>
          ) : null}
          {(imageFiles.length > 0 || pendingImageFiles.length > 0) && pdfFiles.length === 0 ? (
            <div className={style.imagesContainer}>
              {imageFiles.map(({ ordinal, image }) => (
                <ImageItem
                  key={`file${ordinal}`}
                  file={{ ordinal, preview: image }}
                  onDelete={({ ordinal }) => this.onDelete(ordinal)}
                />
              ))}
              {pendingImageFiles.map(({ file, id }) => (
                <ImageItem key={`preview${id}`} loading file={file} />
              ))}
            </div>
          ) : null}

          {pdfFiles.length > 0 ? (
            <PDFItem onDelete={this.onDeletePODPdf} />
          ) : (
            <Dropzone
              className={localClassName.dropzone}
              accept={this.getFilesTypeAccepted()}
              activeClassName={style.dropzoneActive}
              rejectClassName={style.dropzoneReject}
              onDrop={this.onDrop}
              maxSize={config.MAX_FILE_SIZE}
            >
              {({ getRootProps, getInputProps }) => {
                return (
                  <div {...getRootProps({ className: localClassName.dropzone })}>
                    {error ? (
                      <div>
                        <span className={style.dropzoneErrorTitle}>{t.error.uploading}</span>
                        <span className={style.dropzoneErrorText}>{t.error.max_size}</span>
                        <span className={style.dropzoneErrorText}>{t.error.different_types}</span>
                      </div>
                    ) : (
                      <div>
                        <span className={style.dropzoneHelp}>{t.drag_and_drop}</span>
                        <span className={style.dropzoneHelpActive}>{t.let_it_go}</span>
                        <Button nude>{t.upload_images}</Button>
                      </div>
                    )}
                    <input {...getInputProps()} />
                  </div>
                );
              }}
            </Dropzone>
          )}

          <Spacer mTop="M" className={style.loadingContainer}>
            {loading || uploading ? <Loading className={style.loading} /> : null}
            <Button
              onClick={this.onSubmit}
              disabled={loading || uploading}
              text={t.save_and_create_pdf}
            />
          </Spacer>
        </Modal>
      </div>
    );
  }
}

CargoManifestForm.propTypes = {
  journey: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  intl: PropTypes.any,
  createJourneyLog: PropTypes.func,
  cargoManifest: PropTypes.object,
};

const actionCreators = {
  createJourneyLog,
};

export default injectIntl(connect(() => ({}), actionCreators)(CargoManifestForm));
